import { useState } from 'react';
import { Collapse, Grid, ListItem, Typography, Paper, Button, CircularProgress, Box, useTheme } from '@mui/material'
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';
import { useFeatureSupported } from '../../../../FeatureFlagsContext';

export default function ErrorDetails(props: any) {
    const { cargoResponse, handleReuploadClick, reuploadLoading, handleRexportClick, rexportLoading } = props;
    const [open, setOpen] = useState<boolean>(true);
    const { t } = useTranslation();
    const theme = useTheme();
    const recallSupported = useFeatureSupported ? useFeatureSupported('Recall') : false;

    const handleClick = () => {
        setOpen(!open);
    };

    const warningBackgroundColor = 'var(--spectre-warning-surface)';
    const warningBorderColor = 'var(--spectre-warning-border)';
    const warningTextColor = theme.palette.warning.dark;
    const warningHeaderTextColor = theme.palette.warning.contrastText;
    const warningBackgroundColorHover = 'var(--spectre-warning-surface-hover)';

    if (cargoResponse && ((cargoResponse.dimensionCode !== "0" || (cargoResponse.uploadCode !== "" && cargoResponse.uploadCode !== "0") || (cargoResponse.exportCode !== "" && cargoResponse.exportCode !== "0")))) {
        return (
            <Grid
                container
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                sx={{ mt: 1 }}
            >
                <Grid item md={11} xs={12}>
                    <Paper elevation={2} sx={{
                        backgroundColor: warningBackgroundColor,
                        backgroundImage: 'none',
                        border: `1px solid ${warningBorderColor}`,
                        borderRadius: 1,
                        overflow: 'hidden'
                    }}>
                        <ListItemButton
                            sx={{
                                backgroundColor: warningBackgroundColor,
                                '&:hover': {
                                    backgroundColor: warningBackgroundColorHover,
                                }
                            }}
                            onClick={handleClick}
                        >
                            <WarningIcon sx={{ color: warningHeaderTextColor, mr: 1 }} />
                            <ListItemText>
                                <Typography color={warningHeaderTextColor} sx={{ fontWeight: 700 }}>
                                    {t('control.centerControl.errorDetails.title')}
                                </Typography>
                            </ListItemText>
                            {open
                                ? <ExpandLess sx={{ color: warningHeaderTextColor }} />
                                : <ExpandMore sx={{ color: warningHeaderTextColor }} />}
                        </ListItemButton>
                        <Collapse in={open} timeout="auto" unmountOnExit sx={{ backgroundColor: warningBackgroundColor }} >
                            <ListItemText sx={{ backgroundColor: warningBackgroundColor, p: 1, pl: 5 }}>
                                {
                                    cargoResponse.dimensionErrors.map((error: { code: string, description: string }) => (
                                        <ListItem key={error.code} sx={{ backgroundColor: warningBackgroundColor }}>
                                            <ListItemText>
                                                <Typography display="inline" sx={{ fontWeight: 500, wordWrap: 'break-word', color: warningTextColor, whiteSpace: 'pre-line' }}>
                                                    <span style={{ fontWeight: 'bold' }}>{error.code}</span>: {error.description}
                                                </Typography>
                                            </ListItemText>
                                        </ListItem>
                                    ))
                                }
                                {
                                    cargoResponse.dimensionCode !== "0" && cargoResponse.dimensionCodeDescription !== "" ?
                                        <ListItem sx={{ backgroundColor: warningBackgroundColor }}>
                                            <ListItemText>
                                                <Typography display="inline" sx={{ fontWeight: 500, wordWrap: "break-word", color: warningTextColor }}>
                                                    <span style={{ fontWeight: 'bold' }}>{t('control.centerControl.errorDetails.dimension')}</span>: {cargoResponse.dimensionCodeDescription}
                                                </Typography>
                                            </ListItemText>
                                        </ListItem>
                                        :
                                        ""
                                }
                                {
                                    cargoResponse.uploadCode !== "0" && cargoResponse.uploadCodeDescription !== "" ?
                                        <ListItem sx={{ backgroundColor: warningBackgroundColor }}>
                                            <Box
                                                display="flex"
                                                flexDirection={{ xs: 'column', sm: 'row' }}
                                                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                                                justifyContent="space-between"
                                                width="100%"
                                                gap={2}
                                            >
                                                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                                                    <Typography display="inline" sx={{ fontWeight: 500, wordWrap: 'break-word', color: warningTextColor }}>
                                                        <span style={{ fontWeight: 'bold' }}>{t('control.centerControl.errorDetails.upload')}</span>: {cargoResponse.uploadCodeDescription}
                                                    </Typography>
                                                </Box>
                                                {recallSupported && (
                                                    <Button
                                                        className="Upload-Export-Blue-Small"
                                                        sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, whiteSpace: 'nowrap' }}
                                                        onClick={handleReuploadClick}
                                                        disabled={reuploadLoading || cargoResponse.uploadCode === "0"}
                                                        size="small"
                                                    >
                                                        {reuploadLoading ? <CircularProgress size={16} sx={{ color: '#FFFFFF', verticalAlign: 'middle' }} /> : (t('control.centerControl.centerBarcodeFieldButtons.reupload') || 'Re-upload')}
                                                    </Button>
                                                )}
                                            </Box>
                                        </ListItem>
                                        :
                                        ""
                                }
                                {
                                    cargoResponse.exportCode !== "0" && cargoResponse.exportCodeDescription !== "" ?
                                        <ListItem sx={{ backgroundColor: warningBackgroundColor }}>
                                            <Box
                                                display="flex"
                                                flexDirection={{ xs: 'column', sm: 'row' }}
                                                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                                                justifyContent="space-between"
                                                width="100%"
                                                gap={2}
                                            >
                                                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                                                    <Typography display="inline" sx={{ fontWeight: 500, wordWrap: 'break-word', color: warningTextColor }}>
                                                        <span style={{ fontWeight: 'bold' }}>{t('control.centerControl.errorDetails.export')}</span>: {cargoResponse.exportCodeDescription}
                                                    </Typography>
                                                </Box>
                                                {recallSupported && (
                                                    <Button
                                                        className="Upload-Export-Blue-Small"
                                                        sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, whiteSpace: 'nowrap' }}
                                                        onClick={handleRexportClick}
                                                        disabled={rexportLoading}
                                                        size="small"
                                                    >
                                                        {rexportLoading ? <CircularProgress size={16} sx={{ color: '#FFFFFF', verticalAlign: 'middle' }} /> : (t('control.centerControl.centerBarcodeFieldButtons.reexport') || 'Re-export')}
                                                    </Button>
                                                )}
                                            </Box>
                                        </ListItem>
                                        :
                                        ""
                                }
                            </ListItemText>
                        </Collapse>
                    </Paper>
                </Grid>
            </Grid>
        )
    } else {
        return <></>
    }
}